import {
  last, find, throttle, sortBy,
} from 'lodash';
import sanitizeHtml from 'sanitize-html';
import api from 'src/api';
import {
  C_ATTACHMENT_MAX_SIZE, C_WEB_ATTACHMENT_MAX_SIZE, C_ATTACHMENT_MAX_COUNT,
} from 'src/store/modules/app';
import {
  commandType, generateUniqueId, auth, toast,
  formattedMessage, notice, isEmpty, channelType,
} from 'src/utils';
import consts from 'src/utils/consts';
import i18n from 'src/features/i18n';
import humanFileSize from 'src/filters/humanFileSize';
import notificationsRegister from 'src/utils/notificationsRegister';
import { BACKEND_HTTP_PATH } from 'src/config';
import {
  getActivityById,
  getAttachmentById,
  ACTIVITY_MESSAGE_ADD,
  ACTIVITY_MESSAGE_UPDATE,
  MARKERS_UPDATE,
  ADD_MESSAGES,
  ADD_INPUT_ATTACHMENTS,
  UPDATE_INPUT_ATTACHMENTS,
  REMOVE_INPUT_ATTACHMENTS,
  CLEAR_INPUT_ATTACHMENTS,
  UPDATE_ACTIVITY,
} from 'src/store/modules/activities';
import {
  ATTACHMENT_ADD,
  ATTACHMENT_UPDATE,
  ATTACHMENT_REMOVE,
} from 'src/store/modules/departments';
import { resumeActivity, UPDATE_META_DATA_FOR_ACTIVITY } from 'src/store/modules/agent';
import { isExternalOperator } from 'src/store/modules/user';
import { focused } from 'src/store/modules/home';

export const FOUND_ANSWER_TEMPLATES_LOADED = 'FOUND_ANSWER_TEMPLATES_LOADED';
export const FOUND_HINT_ANSWER_TEMPLATES_LOADED = 'FOUND_HINT_ANSWER_TEMPLATES_LOADED';

const data = {
  answerTemplates: [],
  removedAttachments: [],
  foundAnswerTemplates: [],
  foundHintAnswerTemplates: [],
  answerTemplatesSearchTerms: [],
  clientCases: [],
  firstActivityDate: undefined,
  casesTotal: 0,
  totalActivitiesCount: undefined,
  pageSize: 0,
};

function send(activityId, msg) {
  return notice.push(activityId, commandType.NEW_MSG, msg);
}

function setIsRead(activityId, ids) {
  return notice.push(activityId, commandType.IS_READ, { ids });
}

function pushTypingEvt(activityId) {
  return notice.push(activityId, commandType.IS_TYPING, { value: true });
}

function sendToDepartment(activityId, msg) {
  return notice.push(activityId, commandType.NEW_MSG_TO_DEPARTMENT, msg);
}

function parseMessage(responseMessage) {
  return formattedMessage(JSON.parse(JSON.stringify(responseMessage)));
}

function formatUnsendMsg(msg) {
  return {
    ...msg,
    unableToSend: true,
  };
}

function formatForbiddenToSendMsg(msg) {
  return {
    ...msg,
    forbiddenToSend: true,
  };
}

function getActivity(rootGetters, activityId) {
  return rootGetters[`activities/${getActivityById}`](activityId);
}

function getAttachment(rootGetters, activityId, temporaryId) {
  return rootGetters[`activities/${getAttachmentById}`]({ activityId, temporaryId });
}

function getMessage(rootGetters, activity, messageId) {
  return find(activity.messages, { id: messageId });
}

function getTemporaryMessage(rootGetters, activityId, temporaryId) {
  const activity = getActivity(rootGetters, activityId);
  return find(activity.messages, { temporaryId });
}
function addMessage(commit, rootGetters, activityId, message) {
  const activity = getActivity(rootGetters, activityId);
  if (!find(activity.messages, { temporaryId: message.temporaryId, id: message.id })) {
    commit(`activities/${ACTIVITY_MESSAGE_ADD}`, {
      activity: getActivity(rootGetters, activityId),
      message,
    }, { root: true });
  }
  return last(activity.messages);
}

function updateMessage(commit, storeMessage, message) {
  commit(`activities/${ACTIVITY_MESSAGE_UPDATE}`, {
    message: storeMessage,
    response: message,
  }, { root: true });
}

function newAttachment(
  commit, dispatch, rootState, rootGetters, attachment, addFunction, updateFunction,
  deleteFunction, isNotExist,
) {
  const {
    activityId, temporaryId,
  } = attachment;
  const alreadyRemoved = rootState.operatorChat.removedAttachments.find((item) => item === temporaryId);
  if (alreadyRemoved) {
    dispatch(deleteFunction, { activityId, temporaryId });
    return;
  }
  if (isNotExist) {
    addFunction(commit, activityId, temporaryId, {
      ...attachment,
      send: true,
    });
  } else {
    updateFunction(commit, rootGetters, activityId, temporaryId, {
      ...attachment,
      send: true,
    });
  }
}

function deleteAttachment(commit, rootState, attachment, deleteFunction, isExist) {
  const {
    activityId, temporaryId,
  } = attachment;
  const alreadyRemoved = rootState.operatorChat.removedAttachments.find((item) => item === temporaryId);
  if (isExist) {
    deleteFunction(commit, activityId, temporaryId);
    commit('ADD_REMOVED_ATTACHMENTS', temporaryId);
  } else if (!alreadyRemoved) {
    toast.error({
      title: i18n.t('T_FILE_DELETE_ERROR'),
      duration: consts.TOAST_NOTIFICATION_DURATION,
    });
  }
}

function invalidAttachment(commit, rootState, rootGetters, attachment, deleteFunction, isExist) {
  const { activityId, temporaryId } = attachment;

  const activity = getActivity(rootGetters, activityId);
  let sizeAttachment;
  if (activity.channelType === 'web') sizeAttachment = rootState.app[C_WEB_ATTACHMENT_MAX_SIZE];
  else sizeAttachment = rootState.app[C_ATTACHMENT_MAX_SIZE];
  const countAttachment = rootState.app[C_ATTACHMENT_MAX_COUNT][activity.channelType] || 1;

  if (attachment.error.includes('invalid_type')) {
    toast.error({
      title: i18n.t('T_FILE_UPLOADING_ERROR'),
      msg: i18n.t('T_ALLOWED_FILE_TYPE_IS'),
      duration: consts.TOAST_NOTIFICATION_DURATION,
      temporaryId,
    });
  } else if (attachment.error.includes('uploads_count_exceeded')) {
    toast.error({
      title: i18n.t('T_FILE_UPLOADING_ERROR'),
      msg: i18n.t('T_ALLOWED_FILE_COUNT_IS', [countAttachment]),
      duration: consts.TOAST_NOTIFICATION_DURATION,
      temporaryId,
    });
  } else if (attachment.error.includes('uploads_size_exceeded')
    || attachment.error.includes('invalid_size')) {
    toast.error({
      title: i18n.t('T_FILE_UPLOADING_ERROR'),
      msg: i18n.t('T_ALLOWED_FILE_SIZE_IS', [humanFileSize(sizeAttachment)]),
      duration: consts.TOAST_NOTIFICATION_DURATION,
      temporaryId,
    });
  }

  deleteAttachment(commit, rootState, attachment, deleteFunction, isExist);
}

function addInputAttachment(commit, activityId, temporaryId, attachment) {
  commit(`activities/${ADD_INPUT_ATTACHMENTS}`, { activityId, temporaryId, attachment }, { root: true });
}

function updateInputAttachment(commit, rootGetters, activityId, temporaryId, attachment) {
  if (!getAttachment(rootGetters, activityId, temporaryId)) return;
  commit(`activities/${UPDATE_INPUT_ATTACHMENTS}`, { activityId, temporaryId, attachment }, { root: true });
}

function deleteInputAttachment(commit, activityId, temporaryId) {
  commit(`activities/${REMOVE_INPUT_ATTACHMENTS}`, { activityId, temporaryId }, { root: true });
}

function onNewAttachment(commit, dispatch, rootState, rootGetters, attachment) {
  const {
    activityId, temporaryId,
  } = attachment;
  const isNotExist = !rootState.activities.inputAttachments[activityId]
    || !rootState.activities.inputAttachments[activityId][temporaryId];
  const removeFile = 'deleteFile';
  newAttachment(
    commit, dispatch, rootState, rootGetters, attachment,
    addInputAttachment, updateInputAttachment, removeFile, isNotExist,
  );
}

function onDeletedAttachment(commit, rootState, attachment) {
  const {
    activityId, temporaryId,
  } = attachment;
  const isExist = rootState.activities.inputAttachments[activityId]
    && rootState.activities.inputAttachments[activityId][temporaryId];
  deleteAttachment(commit, rootState, attachment, deleteInputAttachment, isExist);
}

function onInvalidAttachment(commit, rootState, rootGetters, attachment) {
  const {
    activityId, temporaryId,
  } = attachment;
  const isExist = rootState.activities.inputAttachments[activityId]
    && rootState.activities.inputAttachments[activityId][temporaryId];
  invalidAttachment(commit, rootState, rootGetters, attachment, deleteInputAttachment, isExist);
}

function addDepartmentAttachment(commit, activityId, temporaryId, attachment) {
  commit(`departments/${ATTACHMENT_ADD}`, { activityId, temporaryId, attachment }, { root: true });
}

function updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, attachment) {
  commit(`departments/${ATTACHMENT_UPDATE}`, { activityId, temporaryId, attachment }, { root: true });
}

function deleteDeparmentAttachment(commit, activityId, temporaryId) {
  commit(`departments/${ATTACHMENT_REMOVE}`, { activityId, temporaryId }, { root: true });
}

function onNewMsg(commit, rootGetters, rootState, dispatch, responseMessage) {
  const message = parseMessage(responseMessage);
  const {
    activityId, temporaryId, sender, id, receiver,
  } = message;
  const activity = getActivity(rootGetters, activityId);
  const isMsgAlreadyInActivity = activity.messages.some((item) => item.id === id);
  const messageFromCurrentActivity = activityId === rootState.agent.selectedActivityId
    || !rootState.agent.selectedActivityId;

  let pushMsg = !isEmpty(message.attachments) ? message.attachments[0].title : message.message;
  if (pushMsg === null) {
    pushMsg = '';
  }

  let customerType = 'anon';
  if (activity.customer.identified) {
    customerType = activity.channelType;
    if (activity.channelType === 'web') customerType = 'client';
  }

  message.customerType = customerType;

  if (temporaryId && sender === 'operator') {
    const storeMessage = getTemporaryMessage(rootGetters, activityId, temporaryId);
    updateMessage(commit, storeMessage, message);
    // вывод из состояния NEW
    commit(`agent/${UPDATE_META_DATA_FOR_ACTIVITY}`, {
      activityId,
      meta: { isNew: false },
    }, { root: true });
  } else {
    const storeMessage = getMessage(rootGetters, activity, id);
    if (storeMessage) {
      updateMessage(commit, storeMessage, message);
    } else {
      addMessage(commit, rootGetters, activityId, message);
    }
  }
  const isInfoMsgForCustomerOnNewActivityAfterTransfer = receiver === 'customer' && isMsgAlreadyInActivity
    && sender === 'info_message';
  if (sender !== 'operator' && !messageFromCurrentActivity && rootGetters[`home/${focused}`]
    && !isInfoMsgForCustomerOnNewActivityAfterTransfer) {
    // условие !isInfoMsgForCustomerOnNewActivityAfterTransfer отсекает те сообщения кастомеру,
    // которые отправляются как автоответы по событию "новое обращение после перевода",
    // тк в случае фокусировки на другой активности
    // фронт уже отобразит их при джойне в оповещениях - чтобы не было дубля оповещений
    // правим на фронте, тк на бэке сложно
    toast.message({
      title: i18n.t('T_NEW_MESSAGE'),
      msg: pushMsg,
      duration: consts.TOAST_NOTIFICATION_DURATION,
    });
  }

  if (sender !== 'operator' && !rootGetters[`home/${focused}`]) {
    notificationsRegister(pushMsg);
  }

  if (sender !== 'operator' && (!rootGetters[`home/${focused}`] || !messageFromCurrentActivity)) {
    dispatch('home/startAutoChangeTitle', i18n.t('T_NEW_MESSAGE'), { root: true });
    dispatch('home/newMessagePlaySound', {}, { root: true });
  }

  // вывод из onhold при поступлении нового сообщения
  dispatch(`agent/${resumeActivity}`, activityId, { root: true });
  // чтобы убрать таймер о скором закрытии/проставлении на холд (если был), когда пришло новое сообщение не от инфо-сендера
  if (sender !== 'info_message') {
    commit(`agent/${UPDATE_META_DATA_FOR_ACTIVITY}`, {
      activityId,
      meta: { preClose: false, preHold: false },
    }, { root: true });
  }
}

// пуш события typing (текущая сторона диалога печатает)
// производится не чаще чем раз в throttleTimeForTypingHandler
const throttleTimeForTypingHandler = 1000;

const typingActivityTimers = {};

function typingOn(dispatch, activityId) {
  // pushUserAction для обновления времени последнего действия оператора
  dispatch('operator/pushUserAction', { activityId, actionName: 'typing' }, { root: true });
  pushTypingEvt(activityId);
}

function onTyping(dispatch, activityId) {
  let fn = typingActivityTimers[activityId];
  if (typeof typingActivityTimers[activityId] === 'undefined') {
    fn = throttle(typingOn, throttleTimeForTypingHandler);
    typingActivityTimers[activityId] = fn;
  }
  return fn(dispatch, activityId);
}

function updateUnredMessage(commit, rootGetters, activityId, list, value) {
  const activity = getActivity(rootGetters, activityId);
  if (activity) {
    return list.map((id) => {
      const storeMessage = getMessage(rootGetters, activity, id);
      if (storeMessage) updateMessage(commit, storeMessage, { isRead: value });
      return id;
    });
  } return list;
}

function onNewDepartAttachment(commit, dispatch, rootState, rootGetters, attachment) {
  const isNotExist = !rootState.departments.attachments.find((item) => item.temporaryId === attachment.temporaryId);
  const removeFile = 'removeFileFromDepartmentController';
  newAttachment(
    commit, dispatch, rootState, rootGetters, attachment,
    addDepartmentAttachment, updateDepartmentAttachment, removeFile, isNotExist,
  );
}

function onDeleteDepartAttachment(commit, rootState, attachment) {
  const isExist = !!rootState.departments.attachments.find((item) => item.temporaryId === attachment.temporaryId);
  deleteAttachment(commit, rootState, attachment, deleteDeparmentAttachment, isExist);
}

function onInvalidDepartAttachment(commit, rootState, rootGetters, attachment) {
  const isExist = !!rootState.departments.attachments.find((item) => item.temporaryId === attachment.temporaryId);
  invalidAttachment(commit, rootState, rootGetters, attachment, deleteDeparmentAttachment, isExist);
}

const actions = {
  onOperatorTyping({ dispatch }, activityId) {
    onTyping(dispatch, activityId);
  },
  read({ commit, rootGetters }, { activityId, list }) {
    if (notice.isSubscribe(activityId, channelType.ROOM)) {
      return new Promise((resolve, reject) => {
        setIsRead(activityId, list).receive('error', () => {
          reject(list);
        });
        resolve();
      })
        .then(() => updateUnredMessage(commit, rootGetters, activityId, list, true))
        .catch(() => {
          throw updateUnredMessage(commit, rootGetters, activityId, list, false);
        });
    } return Promise.resolve();
  },
  send({ commit, rootGetters }, { activityId, body, attachments }) {
    if (body || attachments) {
      const sanitizedBody = sanitizeHtml(body);
      const attachmentsFilter = attachments && Object.keys(attachments).length !== 0
        ? Object.keys(attachments).map((key) => attachments[key]) : [];
      const putMessage = {
        temporaryId: generateUniqueId(10),
        body: sanitizedBody,
        activityId,
        insertedAt: new Date(),
        sender: 'operator',
        attachments: attachmentsFilter,
        isOperatorSending: true,
      };

      const message = formattedMessage(putMessage);
      const storeMessage = addMessage(commit, rootGetters, activityId, message);
      const unableToSendMsg = formatUnsendMsg(message);
      commit(`activities/${CLEAR_INPUT_ATTACHMENTS}`, { activityId }, { root: true });

      send(activityId, putMessage)
        .receive('ok', (responseMessage) => {
          updateMessage(commit, storeMessage, parseMessage(responseMessage));
        })
        .receive('error', () => {
          updateMessage(commit, storeMessage, unableToSendMsg);
        })
        .receive('timeout', () => {
          updateMessage(commit, storeMessage, unableToSendMsg);
        });
    }
  },
  resend({ commit, rootGetters }, messages) {
    if (messages) {
      messages.forEach((message) => {
        const putMessage = {
          ...message,
          body: message.message,
          insertedAt: new Date(),
          isOperatorSending: true,
        };

        const messageFormatted = formattedMessage(putMessage);
        const forbiddenToSendMsg = formatForbiddenToSendMsg(message);
        const { activityId, temporaryId } = message;
        if (message.receiver === 'department') {
          sendToDepartment(activityId, putMessage)
            .receive('ok', (responseMessage) => {
              const storeMessage = getTemporaryMessage(rootGetters, activityId, temporaryId);
              updateMessage(commit, storeMessage, parseMessage(responseMessage));
            })
            .receive('error', () => {
              commit('home/SET_CONNECTED', false, { root: true });
            })
            .receive('timeout', () => {
              commit('home/SET_CONNECTED', false, { root: true });
            });
        } else {
          send(activityId, putMessage)
            .receive('ok', (responseMessage) => {
              updateMessage(commit, messageFormatted, parseMessage(responseMessage));
            })
            .receive('error', () => {
              commit('home/SET_CONNECTED', false, { root: true });
              updateMessage(commit, messageFormatted, forbiddenToSendMsg);
            })
            .receive('timeout', () => {
              commit('home/SET_CONNECTED', false, { root: true });
              updateMessage(commit, messageFormatted, forbiddenToSendMsg);
            });
        }
      });
    }
  },
  sendFileToDepartmentController({ rootState, rootGetters, commit }, { activityId, files }) {
    let attachments = [];
    const currentAttachments = rootState.departments.attachments;
    let filesSize = currentAttachments && currentAttachments.length !== 0
      ? currentAttachments.map((attachment) => attachment.size)
        .reduce((prev, curr) => prev + curr) : 0;

    if (files.length) {
      attachments = Object.keys(files).map((count) => {
        filesSize += files[count].size;
        return ({
          activityId,
          type: 'file',
          preview: undefined,
          title: files[count].name,
          ext: files[count].name.slice((Math.max(0, files[count].name.lastIndexOf('.')) || Infinity) + 1),
          size: files[count].size,
          progress: 0,
          temporaryId: generateUniqueId(10),
          send: undefined,
          file: files[count],
        });
      });
    } else {
      return;
    }

    const filesCount = currentAttachments && currentAttachments.length !== 0
      ? currentAttachments.length + files.length : files.length;

    const activity = getActivity(rootGetters, activityId);
    let sizeAttachment;
    if (activity.channelType === 'web') sizeAttachment = rootState.app[C_WEB_ATTACHMENT_MAX_SIZE];
    else sizeAttachment = rootState.app[C_ATTACHMENT_MAX_SIZE];
    const countAttachment = rootState.app[C_ATTACHMENT_MAX_COUNT][activity.channelType];

    if (filesCount > countAttachment) {
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_COUNT_IS', [countAttachment]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
      return;
    }
    if (filesSize > sizeAttachment) {
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_SIZE', [humanFileSize(sizeAttachment)]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
      return;
    }

    Object.keys(files).forEach((key) => {
      const { temporaryId } = attachments[key];
      addDepartmentAttachment(commit, activityId, temporaryId, attachments[key]);
      const formData = new FormData();
      formData.append('attachment', files[key]);
      formData.append('temporary_id', temporaryId);
      formData.append('activity_id', activityId);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BACKEND_HTTP_PATH}attachment/department`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
      xhr.upload.onprogress = (event) => {
        updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
          progress: (event.loaded / event.total) * 0.99,
        });
      };
      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: 1,
          });
        } else if (xhr.response === '"invalid_type"') {
          toast.error({
            title: i18n.t('T_FILE_UPLOADING_ERROR'),
            msg: i18n.t('T_ALLOWED_FILE_TYPE_IS'),
            duration: consts.TOAST_NOTIFICATION_DURATION,
            temporaryId,
          });
          updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: -1,
            send: false,
          });
        } else {
          toast.error({
            title: i18n.t('T_FILE_UPLOADING_ERROR'),
            duration: consts.TOAST_NOTIFICATION_DURATION,
            temporaryId,
          });
          updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: -1,
            send: false,
          });
        }
      };
      xhr.onerror = () => {
        commit('home/SET_CONNECTED', false, { root: true });
        updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
          progress: -1,
          send: false,
        });
      };
      xhr.send(formData);
    });
  },
  removeFileFromDepartmentController({ commit }, { activityId, temporaryId }) {
    const formData = new FormData();
    formData.append('temporary_id', temporaryId);
    formData.append('activity_id', activityId);
    const xhr = new XMLHttpRequest();
    xhr.open('DELETE', `${BACKEND_HTTP_PATH}attachment/department`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
    xhr.onerror = () => {
      commit('home/SET_CONNECTED', false, { root: true });
    };
    xhr.send(formData);
  },
  resendFileToDepartmentController({ rootState, rootGetters, commit }, { activityId, temporaryId }) {
    const attachment = rootState.departments.attachments.find((item) => item.temporaryId === temporaryId);
    const fileSize = attachment.size;

    const activity = getActivity(rootGetters, activityId);
    let sizeAttachment;
    if (activity.channelType === 'web') sizeAttachment = rootState.app[C_WEB_ATTACHMENT_MAX_SIZE];
    else sizeAttachment = rootState.app[C_ATTACHMENT_MAX_SIZE];

    if (fileSize > sizeAttachment) {
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_SIZE', [humanFileSize(sizeAttachment)]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
        temporaryId,
      });
      return;
    }

    const formData = new FormData();
    formData.append('attachment', attachment.file);
    formData.append('temporary_id', temporaryId);
    formData.append('activity_id', activityId);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BACKEND_HTTP_PATH}attachment/department`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
    xhr.upload.onprogress = (event) => {
      updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
        progress: (event.loaded / event.total) * 0.99,
      });
    };
    xhr.onload = () => {
      if (xhr.status === 200) {
        updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
          progress: 1,
        });
      } else if (xhr.response === '"invalid_type"') {
        toast.error({
          title: i18n.t('T_FILE_UPLOADING_ERROR'),
          msg: i18n.t('T_ALLOWED_FILE_TYPE_IS'),
          duration: consts.TOAST_NOTIFICATION_DURATION,
          temporaryId,
        });
        updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
          progress: -1,
          send: false,
        });
      } else {
        toast.error({
          title: i18n.t('T_FILE_UPLOADING_ERROR'),
          duration: consts.TOAST_NOTIFICATION_DURATION,
          temporaryId,
        });
        updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
          progress: -1,
          send: false,
        });
      }
    };
    xhr.onerror = () => {
      commit('home/SET_CONNECTED', false, { root: true });
      updateDepartmentAttachment(commit, rootGetters, activityId, temporaryId, {
        progress: -1,
        send: false,
      });
    };
    xhr.send(formData);
  },
  sendToDepartment({ commit, rootGetters }, {
    activityId, body, to, attachments,
  }) {
    if (body) {
      const sanitizedBody = sanitizeHtml(body);
      const putMessage = {
        temporaryId: generateUniqueId(10),
        body: sanitizedBody,
        activityId,
        to,
        insertedAt: new Date(),
        sender: 'operator',
        receiver: 'department',
        attachments,
        isOperatorSending: true,
      };

      const message = formattedMessage(putMessage);
      const storeMessage = addMessage(commit, rootGetters, activityId, message);
      const unableToSendMsg = formatUnsendMsg(message);

      sendToDepartment(activityId, putMessage)
        .receive('ok', (responseMessage) => {
          updateMessage(commit, storeMessage, parseMessage(responseMessage));
        })
        .receive('error', () => {
          updateMessage(commit, storeMessage, unableToSendMsg);
        })
        .receive('timeout', () => {
          updateMessage(commit, storeMessage, unableToSendMsg);
        });
    }
  },
  sendFile({ commit, rootGetters, rootState }, { activityId, files }) {
    let attachments = [];
    const currentAttachments = rootState.activities.inputAttachments[activityId];
    let filesSize = currentAttachments && Object.keys(currentAttachments).length !== 0
      ? Object.keys(currentAttachments).map((temporaryId) => currentAttachments[temporaryId].size)
        .reduce((prev, curr) => prev + curr) : 0;

    if (files.length) {
      attachments = Object.keys(files).map((count) => {
        filesSize += files[count].size;
        return ({
          activityId,
          type: 'file',
          preview: undefined,
          title: files[count].name,
          ext: files[count].name.slice((Math.max(0, files[count].name.lastIndexOf('.')) || Infinity) + 1),
          size: files[count].size,
          progress: 0,
          temporaryId: generateUniqueId(10),
          send: undefined,
          file: files[count],
        });
      });
    } else {
      return;
    }

    const filesCount = currentAttachments && Object.keys(currentAttachments).length !== 0
      ? Object.keys(currentAttachments).length + files.length : files.length;

    const activity = getActivity(rootGetters, activityId);
    let sizeAttachment;
    if (activity.channelType === 'web') sizeAttachment = rootState.app[C_WEB_ATTACHMENT_MAX_SIZE];
    else sizeAttachment = rootState.app[C_ATTACHMENT_MAX_SIZE];
    const countAttachment = rootState.app[C_ATTACHMENT_MAX_COUNT][activity.channelType] || 1;

    if (filesCount > countAttachment) {
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_COUNT_IS', [countAttachment]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
    } else if (filesSize > sizeAttachment) {
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_SIZE_IS', [humanFileSize(sizeAttachment)]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
    } else {
      Object.keys(files).forEach((key) => {
        const { temporaryId } = attachments[key];
        addInputAttachment(commit, activityId, temporaryId, attachments[key]);
        const formData = new FormData();
        formData.append('temporary_id', temporaryId);
        formData.append('activity_id', activityId);
        formData.append('attachment', files[key]);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_HTTP_PATH}attachment/operator_uploads`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
        xhr.upload.onprogress = (event) => {
          updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: (event.loaded / event.total) * 0.99,
          });
        };
        xhr.onload = () => {
          if (xhr.status === 201) {
            updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
              progress: 1,
            });
          } else {
            updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
              progress: -1,
              send: false,
            });
          }
        };
        xhr.onerror = () => {
          toast.error({
            title: i18n.t('T_FILE_UPLOADING_ERROR'),
            duration: consts.TOAST_NOTIFICATION_DURATION,
            temporaryId,
          });
          updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: -1,
            send: false,
          });
        };
        xhr.send(formData);
      });
    }
  },
  deleteFile({ commit, rootState }, { activityId, temporaryId }) {
    const formData = new FormData();
    formData.append('temporary_id', temporaryId);
    formData.append('activity_id', activityId);

    const xhr = new XMLHttpRequest();
    xhr.open('DELETE', `${BACKEND_HTTP_PATH}attachment/operator_uploads`, true);
    xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
    xhr.onload = () => {
      if (xhr.status !== 202) {
        toast.error({
          title: i18n.t('T_FILE_DELETE_ERROR'),
          duration: consts.TOAST_NOTIFICATION_DURATION,
        });
      }
    };
    xhr.onerror = () => {
      if (rootState.activities.inputAttachments[activityId]
        && rootState.activities.inputAttachments[activityId][temporaryId]
        && rootState.activities.inputAttachments[activityId][temporaryId].send !== true) {
        deleteInputAttachment(commit, activityId, temporaryId);
        return;
      }
      toast.error({
        title: i18n.t('T_FILE_DELETE_ERROR'),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
    };
    xhr.send(formData);
  },
  resendAttachment({ commit, rootGetters, rootState }, { activityId, temporaryId }) {
    if (!rootState.activities.inputAttachments[activityId]
      || !rootState.activities.inputAttachments[activityId][temporaryId]) {
      return;
    }
    const attachment = rootState.activities.inputAttachments[activityId][temporaryId];

    const activity = getActivity(rootGetters, activityId);
    let sizeAttachment;
    if (activity.channelType === 'web') sizeAttachment = rootState.app[C_WEB_ATTACHMENT_MAX_SIZE];
    else sizeAttachment = rootState.app[C_ATTACHMENT_MAX_SIZE];
    const filesSize = attachment.size;

    if (filesSize < sizeAttachment) {
      const formData = new FormData();
      formData.append('temporary_id', temporaryId);
      formData.append('activity_id', activityId);
      formData.append('provider', activity.channelType);
      formData.append('attachment', attachment.file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BACKEND_HTTP_PATH}attachment/operator_uploads`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
      xhr.upload.onprogress = (event) => {
        updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
          progress: (event.loaded / event.total) * 0.99,
        });
      };
      xhr.onload = () => {
        if (xhr.status === 201) {
          updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: 1,
          });
        } else {
          updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: -1,
            send: false,
          });
        }
        if (xhr.status === 422) {
          updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: -1,
            send: false,
          });
          toast.error({
            title: i18n.t('T_FILE_UPLOADING_ERROR'),
            msg: i18n.t('T_ALLOWED_FILE_TYPE_IS'),
            duration: consts.TOAST_NOTIFICATION_DURATION,
            temporaryId,
          });
        }
        if (xhr.status === 409) {
          updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
            progress: -1,
            send: false,
          });
          toast.error({
            title: i18n.t('T_FILE_UPLOADING_ERROR'),
            msg: i18n.t('T_FILE_ALREADY_EXISTS'),
            duration: consts.TOAST_NOTIFICATION_DURATION,
            temporaryId,
          });
        }
      };
      xhr.onerror = () => {
        updateInputAttachment(commit, rootGetters, activityId, temporaryId, {
          progress: -1,
          send: false,
        });
      };
      xhr.send(formData);
    } else {
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_SIZE_IS', [humanFileSize(sizeAttachment)]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
    }
  },
  clearAllUploads({ rootState }, { activitiesList }) {
    const activitiesIds = activitiesList.filter((item) => {
      const activityId = item.id;
      const inputAttachments = rootState.activities.inputAttachments[activityId];
      return (!inputAttachments || Object.keys(inputAttachments).length === 0);
    }).map((item) => item.id);
    if (activitiesIds.length) {
      activitiesIds.forEach((id) => {
        api.clearUploads(id);
      });
    }
  },
  update({
    commit, rootGetters, rootState, dispatch,
  }, event) {
    switch (event.type) {
      case commandType.NEW_MSG:
        onNewMsg(commit, rootGetters, rootState, dispatch, event);
        break;
      case commandType.NEW_MSG_FROM_DEPARTMENT:
        onNewMsg(commit, rootGetters, rootState, dispatch, event);
        break;
      case commandType.IS_READ:
        updateUnredMessage(commit, rootGetters, event.activityId, event.ids, true);
        break;
      case commandType.NEW_ATTACHMENT:
        onNewAttachment(commit, dispatch, rootState, rootGetters, event.data);
        break;
      case commandType.DELETED_ATTACHMENT:
        onDeletedAttachment(commit, rootState, event.data);
        break;
      case commandType.INVALID_ATTACHMENT:
        onInvalidAttachment(commit, rootState, rootGetters, event.data);
        break;
      case commandType.NEW_DEPARTMENT_ATTACHMENT:
        onNewDepartAttachment(commit, dispatch, rootState, rootGetters, event.data);
        break;
      case commandType.DELETED_DEPARTMENT_ATTACHMENT:
        onDeleteDepartAttachment(commit, rootState, event.data);
        break;
      case commandType.INVALID_DEPARTMENT_ATTACHMENT:
        onInvalidDepartAttachment(commit, rootState, rootGetters, event.data);
        break;
      default:
        break;
    }
  },
  async loadAnswerTemplates({ commit }) {
    const answerTemplates = await api.getAnswerTemplates();
    commit('SET_ANSWER_TEMPLATES', answerTemplates);
  },
  updateMarkers({ commit, rootGetters }, { activityId, markers }) {
    const activity = getActivity(rootGetters, activityId);
    commit(`activities/${MARKERS_UPDATE}`, {
      activity,
      response: markers,
    }, { root: true });
  },
  async loadMsgByPage({ rootGetters, commit }, { activityId, count, currentCase }) {
    const activity = getActivity(rootGetters, activityId);
    const { messages, customer } = activity;
    let cursorMessage = messages[0] && messages[0].id;
    if (!cursorMessage) {
      const isExternalOperatorVal = rootGetters[`user/${isExternalOperator}`];
      await api.getCustomerCases(customer.id, isExternalOperatorVal).then((cases) => {
        const lastCase = sortBy(cases, [(item) => (item.lastNonSystemMessage
          ? item.lastNonSystemMessage.insertedAt : '')]);
        cursorMessage = last(lastCase).lastNonSystemMessage
          && +last(lastCase).lastNonSystemMessage.id + 1;
      });
    }
    if (cursorMessage) {
      const pageSize = count || 5;
      api.getCustomerCasesPagination({
        customerId: customer.id, page: 1, pageSize, currentCase,
      }).then((response) => {
        const messageArrays = response.customerCases.map((caseItem, index) => caseItem.messages.map((msg) => {
          const message = msg;
          const customerType = undefined;
          const isCurrent = index !== 0;
          const currentID = caseItem.id;
          return formattedMessage(message, customerType, currentID, isCurrent);
        }));
        const loadedMsg = messageArrays.flat();
        commit(`activities/${ADD_MESSAGES}`, {
          activity,
          messages: loadedMsg,
        }, { root: true });
        commit('SET_CLIENT_CASES', {
          clientCases: response.customerCases,
          total: response.totalCount,
          totalActivitiesCount: response.totalActivitiesCount,
          firstActivityDate: response.firstActivityDate,
          pageSize: response.pageSize,
        });
      });
    }
  },
  sendLink(_, activityId) {
    api.getCustomerTokenLink({ activityId });
  },
  async searchAnswerTemplates({ commit }, text) {
    return api.searchAnswerTemplates(text).then((res) => {
      commit(FOUND_ANSWER_TEMPLATES_LOADED, res);
      return res;
    }).catch((err) => {
      throw err;
    });
  },
  async searchHintAnswerTemplates({ commit }, text) {
    return api.searchAnswerTemplates(text).then((res) => {
      commit(FOUND_HINT_ANSWER_TEMPLATES_LOADED, res);
      return res;
    }).catch((err) => {
      throw err;
    });
  },
  async updateCaseComment({ commit, rootGetters }, { activityId, caseId, caseComment }) {
    api.updateCaseComment(caseId, caseComment).then((res) => {
      commit(`activities/${UPDATE_ACTIVITY}`, {
        activity: getActivity(rootGetters, activityId),
        response: {
          case: res,
        },
      }, { root: true });
    }).catch((err) => {
      throw err;
    });
  },
};

const mutations = {
  SET_ANSWER_TEMPLATES(state, value) {
    state.answerTemplates = value;
  },
  ADD_REMOVED_ATTACHMENTS(state, value) {
    state.removedAttachments.push(value);
  },
  FOUND_ANSWER_TEMPLATES_LOADED(state, response) {
    const { terms, answerTemplates } = response;
    state.foundAnswerTemplates = answerTemplates;
    state.answerTemplatesSearchTerms = terms;
  },
  CLEAR_ANSWER_TEMPLATES_LOADED(state) {
    state.foundAnswerTemplates = [];
    state.answerTemplatesSearchTerms = [];
  },
  FOUND_HINT_ANSWER_TEMPLATES_LOADED(state, response) {
    const { answerTemplates } = response;
    state.foundHintAnswerTemplates = answerTemplates;
  },
  CLEAR_HINT_ANSWER_TEMPLATES_LOADED(state) {
    state.foundHintAnswerTemplates = [];
  },
  SET_CLIENT_CASES(state, {
    clientCases, total, totalActivitiesCount, firstActivityDate, pageSize,
  }) {
    state.clientCases = clientCases;
    state.casesTotal = total;
    state.totalActivitiesCount = totalActivitiesCount;
    state.firstActivityDate = firstActivityDate;
    state.pageSize = pageSize;
  },
};

const store = {
  state: data,
  actions,
  mutations,
  namespaced: true,
};

export default store;
